/**
 * Nuvio instant search: products, collections/categories, pages and suggested searches.
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return;
    this.form = this.container.querySelector('form.searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.defaultTab = this.container.querySelector('.side-panel-content--initial');
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');
    this.abortController = null;
    if (!this.form || !this.input || !this.predictiveSearchResults) return;
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce(() => this.onChange(), 150));
    this.input.addEventListener('focus', () => this.onFocus());
    this.button.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
        if (this.container.classList.contains('active')) {
          setTimeout(() => this.input.focus({ preventScroll: true }), 100);
          dispatchCustomEvent('search:open');
        }
        return false;
      });
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  showInitial() {
    this.predictiveSearchResults.classList.remove('active', 'loading');
    this.predictiveSearchResults.innerHTML = '';
    this.predictiveSearchResults.hidden = true;
    if (this.defaultTab) this.defaultTab.hidden = false;
  }

  showResults() {
    if (this.defaultTab) this.defaultTab.hidden = true;
    this.predictiveSearchResults.hidden = false;
    this.predictiveSearchResults.classList.add('active');
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      if (this.abortController) this.abortController.abort();
      this.showInitial();
      return;
    }
    this.showResults();
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }
    const prefixInput = this.form.querySelector('input[name="options[prefix]"]');
    if (prefixInput) prefixInput.value = 'last';
    this.input.value = this.getQuery()
      .split(/\s+/)
      .filter(Boolean)
      .map((term) => term.endsWith('*') ? term : term + '*')
      .join(' ');
  }

  onFocus() {
    if (this.getQuery().length) this.onChange();
  }

  getSearchResults(searchTerm) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.predictiveSearchResults.classList.add('loading');

    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('resources[type]', 'product,collection,page,query');
    params.set('resources[limit]', '10');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][prefix]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku,body');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('section_id', 'predictive-search');

    fetch(theme.routes.predictive_search_url + '?' + params.toString(), {
      signal: this.abortController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Predictive search returned ' + response.status);
        return response.text();
      })
      .then((text) => {
        const documentResult = new DOMParser().parseFromString(text, 'text/html');
        const section = documentResult.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section was not returned');
        this.renderSearchResults(section.innerHTML);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          this.predictiveSearchResults.innerHTML = '<p class="nuvio-search-empty">No instant results found. Press Enter to search.</p>';
          this.showResults();
        }
      })
      .finally(() => this.predictiveSearchResults.classList.remove('loading'));
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.showResults();
    const submitButton = this.predictiveSearchResults.querySelector('#search-results-submit');
    if (submitButton) submitButton.addEventListener('click', () => this.form.requestSubmit());
  }

  close() {
    this.container.classList.remove('active');
  }
}

const initialiseNuvioPredictiveSearch = () => {
  if (!window.nuvioPredictiveSearch) window.nuvioPredictiveSearch = new PredictiveSearch();
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialiseNuvioPredictiveSearch, { once: true });
} else {
  initialiseNuvioPredictiveSearch();
}


/**
 * Predictive results for the custom centred Nuvio header search modal.
 * Kept separate from the legacy drawer so either header can operate safely.
 */
(() => {
  const initNuvioHeaderSearch = () => {
    document.querySelectorAll('[data-nv-search]').forEach((modal) => {
      if (modal.dataset.predictiveReady === 'true') return;
      const form = modal.querySelector('form[role="search"]');
      const input = form && form.querySelector('input[type="search"][name="q"]');
      if (!form || !input) return;
      modal.dataset.predictiveReady = 'true';

      let prefix = form.querySelector('input[name="options[prefix]"]');
      if (!prefix) {
        prefix = document.createElement('input');
        prefix.type = 'hidden';
        prefix.name = 'options[prefix]';
        form.appendChild(prefix);
      }
      prefix.value = 'last';

      const results = document.createElement('div');
      results.className = 'nv-live-search-results';
      results.id = input.id + '-results';
      results.setAttribute('role', 'listbox');
      results.setAttribute('aria-live', 'polite');
      results.hidden = true;
      form.appendChild(results);
      input.setAttribute('aria-controls', results.id);
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');

      let timer;
      let controller;
      const escapeHTML = (value) => {
        const node = document.createElement('span');
        node.textContent = value == null ? '' : String(value);
        return node.innerHTML;
      };
      const clearResults = () => {
        results.hidden = true;
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
      };
      const showMessage = (message) => {
        results.innerHTML = '<p class="nv-live-search-status">' + escapeHTML(message) + '</p>';
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };
      const imageMarkup = (product) => {
        const image = product.image || product.featured_image;
        const source = typeof image === 'string' ? image : image && image.url;
        return source ? '<img src="' + escapeHTML(source) + '" alt="" loading="lazy" width="152" height="152">' : '';
      };
      const priceMarkup = (product) => {
        const value = Number.parseFloat(product.price);
        if (!Number.isFinite(value)) return '';
        try {
          return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
        } catch (_) {
          return '$' + value.toFixed(2);
        }
      };
      const renderProducts = (products, query) => {
        if (!products.length) {
          showMessage('No products found. Try another word.');
          return;
        }
        const cards = products.slice(0, 8).map((product) =>
          '<a class="nv-live-search-item" role="option" href="' + escapeHTML(product.url) + '">' +
            '<span class="nv-live-search-image">' + imageMarkup(product) + '</span>' +
            '<span class="nv-live-search-copy">' +
              '<span class="nv-live-search-title">' + escapeHTML(product.title) + '</span>' +
              '<span class="nv-live-search-price">' + escapeHTML(priceMarkup(product)) + '</span>' +
            '</span>' +
          '</a>'
        ).join('');
        const allURL = theme.routes.search_url + '?q=' + encodeURIComponent(query + '*') + '&options%5Bprefix%5D=last';
        results.innerHTML = '<div class="nv-live-search-grid">' + cards + '</div>' +
          '<a class="nv-live-search-all" href="' + allURL + '">View all results</a>';
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };
      const searchProducts = () => {
        const query = input.value.trim();
        if (query.length < 2) {
          if (controller) controller.abort();
          clearResults();
          return;
        }
        if (controller) controller.abort();
        controller = new AbortController();
        showMessage('Finding products…');
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '8');
        params.set('resources[options][prefix]', 'last');
        params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku,body');
        params.set('resources[options][unavailable_products]', 'last');
        fetch(theme.routes.predictive_search_url + '.json?' + params.toString(), {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        })
          .then((response) => {
            if (!response.ok) throw new Error('Search returned ' + response.status);
            return response.json();
          })
          .then((data) => {
            if (input.value.trim() !== query) return;
            const products = data && data.resources && data.resources.results
              ? data.resources.results.products || []
              : [];
            renderProducts(products, query);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') showMessage('Search is temporarily unavailable. Please try again.');
          });
      };
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(searchProducts, 160);
      });
      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2) searchProducts();
      });
      form.addEventListener('submit', () => {
        prefix.value = 'last';
        const query = input.value.trim();
        if (query) {
          input.value = query.split(/\s+/).filter(Boolean)
            .map((term) => term.endsWith('*') ? term : term + '*').join(' ');
        }
      });
    });
  };

  const style = document.createElement('style');
  style.textContent = `
    [data-nv-search] .nv-search-panel{width:min(940px,92vw)}
    [data-nv-search] .nv-live-search-results{margin-top:18px;padding-top:18px;border-top:1px solid rgba(33,79,38,.14);max-height:min(54vh,520px);overflow:auto;overscroll-behavior:contain}
    [data-nv-search] .nv-live-search-results[hidden]{display:none}
    [data-nv-search] .nv-live-search-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
    [data-nv-search] .nv-live-search-item{display:grid;grid-template-columns:76px minmax(0,1fr);gap:13px;align-items:center;min-height:92px;padding:8px;border:1px solid rgba(33,79,38,.11);border-radius:10px;background:#fff;color:#214f26;text-decoration:none;transition:border-color .15s ease,box-shadow .15s ease}
    [data-nv-search] .nv-live-search-item:hover,[data-nv-search] .nv-live-search-item:focus{border-color:rgba(33,79,38,.42);box-shadow:0 7px 18px rgba(33,79,38,.08);outline:none}
    [data-nv-search] .nv-live-search-image{display:block;width:76px;height:76px;background:#fff;border-radius:7px}
    [data-nv-search] .nv-live-search-image img{display:block;width:76px;height:76px;object-fit:contain;background:#fafafa;border-radius:7px}
    [data-nv-search] .nv-live-search-title{display:block;font-size:13px;font-weight:650;line-height:1.3}
    [data-nv-search] .nv-live-search-price{display:block;margin-top:6px;font-size:12px;color:#625d56}
    [data-nv-search] .nv-live-search-status{margin:0;padding:12px 2px;font-size:13px;color:#625d56}
    [data-nv-search] .nv-live-search-all{display:flex;align-items:center;justify-content:center;margin-top:12px;min-height:42px;border:1px solid #214f26;border-radius:8px;color:#214f26;text-decoration:none;font-size:11px;font-weight:650;letter-spacing:.12em;text-transform:uppercase}
    @media(max-width:700px){
      [data-nv-search] .nv-search-panel{top:8vh!important;padding:26px 18px!important;width:min(720px,94vw)!important}
      [data-nv-search] .nv-live-search-grid{grid-template-columns:1fr}
      [data-nv-search] .nv-live-search-results{max-height:55vh}
    }
  `;
  document.head.appendChild(style);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNuvioHeaderSearch, { once: true });
  } else {
    initNuvioHeaderSearch();
  }
  document.addEventListener('shopify:section:load', initNuvioHeaderSearch);
})();
